/* eslint-disable react/jsx-props-no-spreading -- wrappers here forward props wholesale */
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { createContext, useContext } from 'react';
import ReactOnRails from 'react-on-rails';

import { canUseDOM } from './helpers/dom';
import { withErrorBoundary } from './helpers/error_boundary';

const RailsContext = createContext(null);

export const useRailsContext = () => useContext(RailsContext);

// arity 2 means an existing react-on-rails render function; wrapping one would mount it as JSX and break it
const isRenderFunction = Component => typeof Component === 'function' && Component.length >= 2;

export const withRailsContext = Component => {
  if (isRenderFunction(Component)) return Component;

  const render = (props, railsContext) => () => {
    const tree = (
      <RailsContext.Provider value={railsContext}>
        <Component {...props} />
      </RailsContext.Provider>
    );

    if (canUseDOM()) return tree;

    // one shared cache across pooled renders emits a page's styles only once; key must stay emotion's default
    return <CacheProvider value={createCache({ key: 'css' })}>{tree}</CacheProvider>;
  };

  return render;
};

export const registerWithRailsContext = components =>
  ReactOnRails.register(
    Object.fromEntries(
      Object.entries(components).map(([name, component]) => [
        name,
        withRailsContext(isRenderFunction(component) ? component : withErrorBoundary(component, name)),
      ])
    )
  );

export default RailsContext;
